import { Card, Typography } from 'antd'
import { format } from 'date-fns'
import { ReviewSection, ReviewSectionProps } from 'features/reporting/types'
import { DraftPdfControls } from 'pages/reporting/reports/rug/components/DraftPdfControls'
import { PublishPdfControls } from 'pages/reporting/reports/rug/components/PublishPdfControls'
import { useDraftPdfGeneration } from 'pages/reporting/reports/rug/hooks/UseDraftPdfGeneration'
import { normalizeSalesforceRobotModel } from 'pages/reporting/reports/rug/hooks/useSalesforceReportData'
import { RugReportSectionId } from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import { getAddendumPages } from 'pages/reporting/reports/rug/utils/addendums/addendumContent'
import { getSensorPayloadStrings } from 'pages/reporting/reports/rug/utils/getSensorPayloadStrings'
import { formatInspectionTemperature } from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { SIGNOFF_ROLE_LABELS } from 'pages/reporting/shared/approval/approvalRules'
import { ReportApprovalPanel } from 'pages/reporting/shared/approval/components/ReportApprovalPanel'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import {
  GeckoMeasurement,
  GeckoMeasurementUnit,
  ReportApprovalWorkflow,
  SignoffRole,
  SignoffState,
} from 'types'

const Text = Typography.Text

const QualificationOptions = [
  'UTT Level II',
  'UTDA Level II',
  'ASNT Level III Ultrasonics',
  'Advanced Services Manager',
  'NDT Technology Manager',
]

const PageSection = styled(Card)`
  margin-bottom: 16px;
`

const SectionContent = styled.div`
  margin-left: 16px;
`

const DataRow = styled.div`
  margin-bottom: 8px;
  display: flex;
  gap: 8px;
`

const formatDate = (date: string | Date | undefined) => {
  if (!date) return 'Not specified'

  try {
    if (typeof date === 'string') {
      return date
    }
    return format(date, 'MM/dd/yyyy')
  } catch {
    return 'Invalid date'
  }
}

const formatCalibrationTime = (timeString: string): string => {
  try {
    const date = new Date(timeString)
    if (isNaN(date.getTime())) return timeString
    return format(date, 'MM/dd/yyyy h:mm a')
  } catch {
    return timeString
  }
}

const RenderReact: React.FC<ReviewSectionProps> = ({ previewMode }) => {
  const {
    formData,
    selectedRevisionData,
    organization,
    unit,
    inspectionMetadata,
    formatMeasurement,
    selectedComponents,
    metadataEntryId,
    reportSignatures,
    updateFormData,
  } = useRugReportOps()

  const selectedFormData = useMemo(
    () =>
      previewMode && selectedRevisionData ? selectedRevisionData : formData,
    [formData, selectedRevisionData, previewMode]
  )

  const draftGeneration = useDraftPdfGeneration()
  const { generateDraft } = draftGeneration
  // The cover prints the assignees, so a saved workflow makes the draft stale
  const handleWorkflowSaved = useCallback(
    (next: {
      approvalWorkflow: ReportApprovalWorkflow
      signoff: SignoffState
    }) => {
      updateFormData(next)
      void generateDraft(next)
    },
    [generateDraft, updateFormData]
  )

  const {
    mainInfo,
    coverSectionData,
    summarySectionData,
    dataSheetSectionData,
    imagesSectionData,
    signoff,
    revisionMetadata,
  } = selectedFormData

  const formatResolutionDimension = (value: number, unit: string) => {
    const isTubeUnit = unit === 'tube' || unit === GeckoMeasurementUnit.Tubes.id
    if (isTubeUnit) {
      return `${value} ${value === 1 ? 'tube' : 'tubes'}`
    }

    return (
      formatMeasurement({
        measurement: GeckoMeasurement.CreateFromValue(
          value,
          GeckoMeasurementUnit.GetOrCreate(
            unit || GeckoMeasurementUnit.Inches.id
          )
        ),
        variant: 'narrow',
        maximumFractionDigits: 2,
        minimumFractionDigits: 0,
      }) ?? `${value}`
    )
  }

  const formatResolution = (
    resolution:
      | {
          width?: number
          height?: number
          widthUnit?: string
          heightUnit?: string
          angles?: number
        }
      | undefined
  ) => {
    if (!resolution?.width || !resolution?.height) {
      return 'Not specified'
    }

    const widthStr = formatResolutionDimension(
      resolution.width,
      resolution.widthUnit ?? ''
    )
    const heightStr = formatResolutionDimension(
      resolution.height,
      resolution.heightUnit ?? ''
    )

    if (resolution.angles) {
      return `${widthStr} x ${resolution.angles} angles x ${heightStr}`
    }

    return `${widthStr} x ${heightStr}`
  }

  const resolutionString = formatResolution(
    summarySectionData.inspectionResolution
  )
  const deliverableResolutionString = formatResolution(
    summarySectionData.deliverableResolution
  )

  const { payloadTypesStr, sensorFrequenciesStr, sensorDiametersStr } =
    getSensorPayloadStrings({
      payloads: dataSheetSectionData?.payloads,
      formatMeasurement,
    })

  const inspectionTemperature = formatInspectionTemperature(
    mainInfo.inspectionTemperature
  )

  const sensorAngle = inspectionMetadata?.sensorPayloadMetadata?.angle

  return (
    <div>
      <PageSection title='Asset Information'>
        <SectionContent>
          <DataRow>
            <Text strong>Customer:</Text>{' '}
            {mainInfo.customerName || organization?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Location:</Text> {mainInfo.customerLocation || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Asset Name:</Text>{' '}
            {mainInfo.assetName || unit?.data.name || 'N/A'}
          </DataRow>
          <DataRow>
            <Text strong>Gecko Job Number:</Text> {mainInfo.jobNumber}
          </DataRow>
          <DataRow>
            <Text strong>Asset Type:</Text> {mainInfo.assetType}
          </DataRow>
          <DataRow>
            <Text strong>Asset Material:</Text> {mainInfo.assetMaterial}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Temperature:</Text> {inspectionTemperature}
          </DataRow>
          {selectedComponents &&
            selectedComponents.map((component) => {
              const axisLocations =
                mainInfo.componentAxisStartLocations?.[component.id]
              return (
                <React.Fragment key={component.id}>
                  <DataRow>
                    <Text strong>
                      X-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.xAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                  <DataRow>
                    <Text strong>
                      Y-Axis Start Location ({component.data.name}):
                    </Text>{' '}
                    {axisLocations?.yAxisStartLocation ?? 'Not specified'}
                  </DataRow>
                </React.Fragment>
              )
            })}
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Start Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionStartDate)}
          </DataRow>
          <DataRow>
            <Text strong>Inspection End Date:</Text>{' '}
            {formatDate(coverSectionData.inspectionEndDate)}
          </DataRow>
          <DataRow>
            <Text strong>Revision Number:</Text>{' '}
            {revisionMetadata.revisionNumber}
          </DataRow>
          <DataRow>
            <Text strong>Publication Status:</Text>{' '}
            {revisionMetadata.isPublished ? 'Published' : 'Draft'}
          </DataRow>
        </SectionContent>
      </PageSection>

      <PageSection title='Inspection Summary'>
        <SectionContent>
          <DataRow>
            <Text strong>Coverage Scope:</Text>{' '}
            {summarySectionData.coverageScope || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Inspection Resolution:</Text> {resolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Deliverable Resolution:</Text>{' '}
            {deliverableResolutionString}
          </DataRow>
          <DataRow>
            <Text strong>Corrosion Summary:</Text>
          </DataRow>
          <DataRow style={{ marginLeft: 16 }}>
            {summarySectionData.corrosionBLUF ||
              'No corrosion summary provided'}
          </DataRow>
          {summarySectionData.inspectionSummaryNotes && (
            <>
              <DataRow>
                <Text strong>Additional Notes:</Text>
              </DataRow>
              <DataRow style={{ marginLeft: 16 }}>
                {summarySectionData.inspectionSummaryNotes}
              </DataRow>
            </>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Technical Details'>
        <SectionContent>
          <DataRow>
            <Text strong>Inspection Surface:</Text>{' '}
            {dataSheetSectionData.inspectionSurface || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Surface Condition:</Text>{' '}
            {dataSheetSectionData.surfaceCondition || 'Not specified'}
          </DataRow>
          <DataRow>
            <Text strong>Coating Present:</Text>{' '}
            {dataSheetSectionData.coatingPresent ? 'Yes' : 'No'}
          </DataRow>

          {dataSheetSectionData.robots &&
            dataSheetSectionData.robots.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Robots Used:</Text>
                </DataRow>
                {dataSheetSectionData.robots.map((robot, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    Robot {index + 1}:{' '}
                    {normalizeSalesforceRobotModel(robot.model) ||
                      'Unknown Model'}{' '}
                    (S/N: {robot.serialNumber})
                  </DataRow>
                ))}
              </>
            )}

          {dataSheetSectionData.calibrationBlocks &&
            dataSheetSectionData.calibrationBlocks.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Blocks:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationBlocks.map((block, idx) => (
                  <DataRow key={idx} style={{ marginLeft: 16 }}>
                    {block.type || 'N/A'} - S/N: {block.serialNumber || 'N/A'}
                  </DataRow>
                ))}
              </>
            )}

          {dataSheetSectionData.calibrationTimes &&
            dataSheetSectionData.calibrationTimes.length > 0 && (
              <DataRow style={{ marginTop: 8, marginLeft: 16 }}>
                Calibration Times:{' '}
                {dataSheetSectionData.calibrationTimes
                  .map(formatCalibrationTime)
                  .join(', ')}
              </DataRow>
            )}

          {dataSheetSectionData.calibrationSteps &&
            dataSheetSectionData.calibrationSteps.length > 0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Calibration Steps:</Text>
                </DataRow>
                {dataSheetSectionData.calibrationSteps.map((step, index) => (
                  <DataRow key={index} style={{ marginLeft: 16 }}>
                    {step.stepName}: {step.distance.value} {step.distance.unit}
                  </DataRow>
                ))}
              </>
            )}

          {dataSheetSectionData.sensorSerialNumbers &&
            Object.keys(dataSheetSectionData.sensorSerialNumbers).length >
              0 && (
              <>
                <DataRow style={{ marginTop: 16 }}>
                  <Text strong>Sensor Serial Numbers:</Text>
                </DataRow>
                {Object.entries(dataSheetSectionData.sensorSerialNumbers).map(
                  ([key, serials]) => (
                    <DataRow key={key} style={{ marginLeft: 16 }}>
                      Sensor {key}: {serials.join(', ')}
                    </DataRow>
                  )
                )}
              </>
            )}
        </SectionContent>
      </PageSection>

      <PageSection title='Metadata'>
        <SectionContent>
          {inspectionMetadata?.inspectionSoftwareVersion && (
            <DataRow>
              <Text strong>Inspection Software Version:</Text>{' '}
              {inspectionMetadata.inspectionSoftwareVersion}
            </DataRow>
          )}
          {inspectionMetadata?.reported_ascan_count !== undefined && (
            <DataRow>
              <Text strong>A-Scan Count:</Text>{' '}
              {inspectionMetadata.reported_ascan_count.toLocaleString()}
            </DataRow>
          )}
          {payloadTypesStr && (
            <DataRow>
              <Text strong>Sensor Payload Type:</Text> {payloadTypesStr}
            </DataRow>
          )}
          {sensorFrequenciesStr && (
            <DataRow>
              <Text strong>Sensor Frequency:</Text> {sensorFrequenciesStr}
            </DataRow>
          )}
          {sensorDiametersStr && (
            <DataRow>
              <Text strong>Sensor Diameter:</Text> {sensorDiametersStr}
            </DataRow>
          )}
          {sensorAngle !== undefined && (
            <DataRow>
              <Text strong>Sensor Angle:</Text> {sensorAngle}°
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Selected Images'>
        <SectionContent>
          {imagesSectionData.imageReportIds &&
          imagesSectionData.imageReportIds.length > 0 ? (
            <DataRow>
              <Text strong>Number of Images:</Text>{' '}
              {imagesSectionData.imageReportIds.length}
            </DataRow>
          ) : (
            <DataRow>
              <Text type='secondary'>No images selected</Text>
            </DataRow>
          )}
        </SectionContent>
      </PageSection>

      <PageSection title='Section Addendums'>
        <SectionContent>
          {(selectedFormData.sectionAddendums ?? []).map((addendum, index) => (
            <div key={addendum.id ?? index}>
              <Text strong>{addendum.sectionId}</Text>
              <DataRow>
                {getAddendumPages(addendum).length} report pages
              </DataRow>
              {addendum.mode === 'attachments' &&
                addendum.attachments?.map((attachment) => (
                  <DataRow key={attachment.id}>
                    {attachment.fileName}
                    {attachment.kind === 'pdf'
                      ? ` (${attachment.pageCount} pages)`
                      : ''}
                  </DataRow>
                ))}
            </div>
          ))}
          {!selectedFormData.sectionAddendums?.length && (
            <Text type='secondary'>No addendums</Text>
          )}
        </SectionContent>
      </PageSection>
      {!previewMode && (
        <>
          <PageSection title='Draft PDF'>
            <DraftPdfControls generation={draftGeneration} />
          </PageSection>
          <PageSection title='Approval & Signatures'>
            <ReportApprovalPanel
              metadataEntryId={metadataEntryId}
              workflow={formData.approvalWorkflow}
              signoff={formData.signoff}
              signatures={reportSignatures}
              qualificationOptions={QualificationOptions}
              reviewSectionId={RugReportSectionId.REVIEW}
              reportTitle={`RUG report for ${mainInfo.assetName || unit?.data.name || unit?.id}`}
              organizationId={organization?.id}
              readOnly={
                revisionMetadata.isPublished ||
                Boolean(selectedFormData.publishedPdfPath)
              }
              onWorkflowSaved={handleWorkflowSaved}
            />
          </PageSection>
          <PageSection title='Publish'>
            <PublishPdfControls />
          </PageSection>
        </>
      )}

      {previewMode && signoff && (
        <PageSection title='Review Details'>
          <SectionContent>
            {signoff.supervisorSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>
                  {SIGNOFF_ROLE_LABELS[SignoffRole.SUPERVISOR]}
                </Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.supervisorSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.supervisorSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.supervisorSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.dataAnalystSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>
                  {SIGNOFF_ROLE_LABELS[SignoffRole.DATA_ANALYST]}
                </Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.dataAnalystSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.dataAnalystSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.dataAnalystSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
            {signoff.reviewerSignoff && (
              <div style={{ marginBottom: '16px' }}>
                <Text strong>{SIGNOFF_ROLE_LABELS[SignoffRole.REVIEWER]}</Text>
                <DataRow>
                  <Text>Name:</Text> {signoff.reviewerSignoff.name}
                </DataRow>
                <DataRow>
                  <Text>Qualifications:</Text>{' '}
                  {signoff.reviewerSignoff.qualifications}
                </DataRow>
                <DataRow>
                  <Text>Date:</Text>{' '}
                  {format(
                    new Date(signoff.reviewerSignoff.timestamp),
                    'MMM dd, yyyy HH:mm:ss'
                  )}
                </DataRow>
              </div>
            )}
          </SectionContent>
        </PageSection>
      )}
    </div>
  )
}

export const ReportReview: ReviewSection = {
  id: RugReportSectionId.REVIEW,
  title: 'Review',
  description: 'Review report data, collect sign-offs, and publish the PDF',
  RenderReact,
  required: true,
}
